import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Chip,
} from '@mui/material';
import { DrawerHeader } from '@shared/components/drawer';
import NoDataFound from '@shared/components/NoDataFound/NoDataFound';
import { useOfferScheduleLogs } from '@modules/offers/hooks';
import { formatDate } from '@modules/offers/utils';
import { LoadingSpinner } from '@shared/components';

interface ScheduleLogsDialogProps {
  open: boolean;
  offerId?: number | string;
  onClose: () => void;
}
export const ScheduleLogsDialog: React.FC<ScheduleLogsDialogProps> = ({ open, offerId, onClose }) => {
  const theme = useTheme();
  // Only fetch when the dialog is open to avoid API calls during list render
  const { data: logs, isLoading, refetch, isError } = useOfferScheduleLogs(open ? offerId : undefined);

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open]);
  // Normalize new API shape to a common structure
  const normalized = React.useMemo(() => {
    const arr = Array.isArray(logs) ? logs : [];
    return arr.map((l: any) => ({
      created_at: l.created_at,
      updated_by: l.updated_by_name,
      module: l.module_type ?? l.type ?? 'unknown',
      changes: l.changed_fields ?? {},
      id: l.system_change_log_id ?? `${l.module_type}-${l.target_id}-${l.created_at}`,
    }));
  }, [logs]);

  const renderVal = (v) => {
    if (v === null || v === undefined)
      return (
        <Typography component="span" sx={{ color: 'text.disabled' }}>
          —
        </Typography>
      );
    // Render ISO dates nicely
    if (typeof v === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      return <>{formatDate(v)}</>;
    }
    if (typeof v === 'object') return <>{JSON.stringify(v)}</>;
    return <>{String(v)}</>;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DrawerHeader title="Audit Trail Logs" onClose={onClose} />
      <DialogContent>
        {isLoading && <LoadingSpinner fullHeight message="Fetching logs..." />}
        {!isLoading && isError && (
          <Typography color="error" variant="body2">
            Failed to load logs.
          </Typography>
        )}
        {!isLoading && !isError && (
          <Box>
            {normalized.length === 0 ? (
              <NoDataFound message="No logs found." />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Changed On</TableCell>
                      <TableCell>Changed By</TableCell>
                      <TableCell>Module</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {normalized.map((log) => (
                      <TableRow key={String(log.id)} hover>
                        <TableCell>{formatDate(log.created_at, true)}</TableCell>
                        <TableCell>{log.updated_by}</TableCell>
                        <TableCell>
                          <Chip
                            label={String(log.module).toUpperCase()}
                            size="small"
                            color={String(log.module).toLowerCase() === 'offer' ? 'primary' : 'info'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {Object.keys(log.changes || {}).length === 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 4 }}
                            >
                              No field changes
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'grid', rowGap: 0.5 }}>
                              {Object.entries(log.changes).map(([field, diff]: any) => (
                                <Box
                                  key={field}
                                  sx={{ display: 'flex', alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
                                    {field}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      maxWidth: '30%',
                                    }}
                                  >
                                    {renderVal(diff.from)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mx: 0.5 }}>
                                    →
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'success.main',
                                      fontWeight: 600,
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      maxWidth: '47%',
                                    }}
                                  >
                                    {renderVal(diff.to)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleLogsDialog;
